import { useEffect, useRef, useState } from "react";
import type { SessionTitleSource } from "../../api/types.js";

export function SessionTitleText({ title, source }: { title: string; source?: SessionTitleSource }) {
  const [displayedTitle, setDisplayedTitle] = useState(title);
  const [animating, setAnimating] = useState(false);
  const previousRef = useRef({ title, source });

  useEffect(() => {
    const previous = previousRef.current;
    previousRef.current = { title, source };
    if (previous.title === title) {
      setDisplayedTitle(title);
      setAnimating(false);
      return;
    }

    const shouldAnimate = source === "generated"
      && (previous.source === "default" || previous.source === "provisional")
      && !window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (!shouldAnimate) {
      setDisplayedTitle(title);
      setAnimating(false);
      return;
    }

    const characters = Array.from(title);
    const intervalMs = Math.max(18, Math.min(36, Math.round(420 / Math.max(1, characters.length))));
    let visibleCharacters = 0;
    setDisplayedTitle("");
    setAnimating(true);
    const timer = window.setInterval(() => {
      visibleCharacters += 1;
      setDisplayedTitle(characters.slice(0, visibleCharacters).join(""));
      if (visibleCharacters >= characters.length) {
        window.clearInterval(timer);
        setAnimating(false);
      }
    }, intervalMs);
    return () => window.clearInterval(timer);
  }, [source, title]);

  return (
    <span
      className={`session-title-text ${animating ? "is-typing" : ""}`}
      aria-hidden="true"
      data-session-title
      data-animating={animating ? "true" : "false"}
    >
      {displayedTitle}
    </span>
  );
}
